<script setup lang="ts">
import { ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useQuizAccessStore } from '@/stores/quizAccess'
import { onMounted } from 'vue'
import Button from 'primevue/button'
import Popover from 'primevue/popover'

const router = useRouter()
const route = useRoute()
const store = useQuizAccessStore()
const { locale, t } = useI18n({ useScope: 'global' })

// Guard: routes that need a token redirect to enter if missing
const GUARDED = [
  'quiz-access-quizzes',
  'quiz-access-quiz',
  'quiz-access-done',
  'quiz-access-materials',
]

onMounted(() => {
  if (GUARDED.includes(route.name as string) && !store.token) {
    router.replace({ name: 'quiz-access-enter' })
  }
})

const langPopover = ref()

const ALL_UI_LANGS = [
  { code: 'bg', label: 'Bulgarian' },
  { code: 'cs', label: 'Czech' },
  { code: 'da', label: 'Danish' },
  { code: 'de', label: 'German' },
  { code: 'el', label: 'Greek' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Spanish' },
  { code: 'et', label: 'Estonian' },
  { code: 'fi', label: 'Finnish' },
  { code: 'fr', label: 'French' },
  { code: 'ga', label: 'Irish' },
  { code: 'hr', label: 'Croatian' },
  { code: 'hu', label: 'Hungarian' },
  { code: 'it', label: 'Italian' },
  { code: 'lt', label: 'Lithuanian' },
  { code: 'lv', label: 'Latvian' },
  { code: 'mt', label: 'Maltese' },
  { code: 'nl', label: 'Dutch' },
  { code: 'pl', label: 'Polish' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'ro', label: 'Romanian' },
  { code: 'sk', label: 'Slovak' },
  { code: 'sl', label: 'Slovenian' },
  { code: 'sv', label: 'Swedish' },
]

function setLocale(code: string) {
  locale.value = code
  localStorage.setItem('ui_locale', code)
  langPopover.value?.hide()
}
</script>

<template>
  <div class="min-h-screen bg-slate-50 flex flex-col">
    <!-- Minimal top bar -->
    <header class="bg-indigo-700 px-4 py-3 flex items-center gap-2.5 shrink-0">
      <img src="/logo.png" alt="supervisor-ng" class="h-7 w-7 object-contain shrink-0" />
      <span class="text-white font-bold text-sm tracking-tight">Supervisor</span>
      <span class="text-white/50 text-xs ml-1">· {{ t('quizAccess.layout.subtitle') }}</span>

      <div class="ml-auto">
        <Button
          text
          rounded
          size="small"
          aria-label="Switch language"
          class="!text-white/80 hover:!text-white hover:!bg-white/10 !p-1"
          @click="langPopover.toggle($event)"
        >
          <span class="text-[11px] font-semibold tracking-wide uppercase">{{ locale }}</span>
        </Button>
        <Popover ref="langPopover">
          <div class="py-1 w-44 max-h-64 overflow-y-auto">
            <button
              v-for="lang in ALL_UI_LANGS"
              :key="lang.code"
              class="w-full flex items-center justify-between px-3 py-1.5 text-sm hover:bg-surface-100 dark:hover:bg-surface-700 rounded transition-colors"
              :class="locale === lang.code ? 'font-semibold text-primary-600' : 'text-surface-700'"
              @click="setLocale(lang.code)"
            >
              <span>{{ lang.label }}</span>
              <span class="text-xs text-surface-400 uppercase">{{ lang.code }}</span>
            </button>
          </div>
        </Popover>
      </div>
    </header>

    <main class="flex-1 flex flex-col">
      <RouterView />
    </main>

    <footer v-if="store.tenant?.privacyPolicyUrl" class="shrink-0 px-4 py-3 text-center">
      <a
        :href="store.tenant.privacyPolicyUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="text-[11px] text-slate-400 hover:text-slate-600 underline underline-offset-2"
      >
        {{ t('quizAccess.tenantPrivacy.link') }}<template v-if="store.tenant.name"> — {{ store.tenant.name }}</template>
      </a>
    </footer>
  </div>
</template>
